import {
  default as React,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";

export type AnchoredSelectValue = string | number | boolean;

export interface AnchoredSelectOption<T extends AnchoredSelectValue> {
  value: T;
  label: string;
  description?: string;
  searchableText?: string;
  disabled?: boolean;
}

interface AnchoredSelectPopoverProps<T extends AnchoredSelectValue> {
  value: T | null;
  options: readonly AnchoredSelectOption<T>[];
  onChange: (value: T | null) => void;
  onBlur?: () => void;
  label?: string;
  ariaLabel: string;
  placeholder: string;
  allowClear?: boolean;
  disabled?: boolean;
  required?: boolean;
  clearLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsLabel?: string;
  testId?: string;
  triggerId?: string;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  portalContainer?: HTMLElement | null;
}

const OPEN_EVENT = "crs:anchored-select:open";
const VIEWPORT_MARGIN = 12;
const ROW_HEIGHT = 44;
const COMPACT_OPTION_LIMIT = 8;

/**
 * Application-owned finite-value selector. The menu is portalled and fixed to
 * the viewport, avoiding clipped menus and native mobile operating-system pickers.
 */
export function AnchoredSelectPopover<T extends AnchoredSelectValue>({
  value,
  options,
  onChange,
  onBlur,
  label,
  ariaLabel,
  placeholder,
  allowClear = false,
  disabled = false,
  required = false,
  clearLabel,
  searchable = false,
  searchPlaceholder,
  noResultsLabel,
  testId,
  triggerId,
  ariaDescribedBy,
  ariaInvalid,
  className = "",
  triggerClassName = "",
  menuClassName = "",
  portalContainer,
}: AnchoredSelectPopoverProps<T>) {
  const instanceId = useId().replace(/:/g, "");
  const menuId = `anchored-select-${instanceId}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const frameRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 220, maxHeight: 304 });

  const usesSearch = searchable || options.length > COMPACT_OPTION_LIMIT;
  const selected = useMemo(() => options.find((option) => option.value === value), [options, value]);
  const visibleOptions = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!usesSearch || !normalized) return options;
    return options.filter((option) => `${option.label} ${String(option.value)} ${option.searchableText ?? ""}`.toLocaleLowerCase().includes(normalized));
  }, [options, query, usesSearch]);
  const firstEnabledIndex = useCallback((items: readonly AnchoredSelectOption<T>[]) => items.findIndex((option) => !option.disabled), []);

  const normalizeActiveIndex = useCallback((candidate: number, items = visibleOptions) => {
    if (!items.length) return -1;
    const firstEnabled = firstEnabledIndex(items);
    if (firstEnabled < 0) return -1;
    const normalized = Math.min(Math.max(candidate, 0), items.length - 1);
    return items[normalized]?.disabled ? firstEnabled : normalized;
  }, [firstEnabledIndex, visibleOptions]);

  const moveActiveIndex = useCallback((offset: number) => {
    if (!visibleOptions.length) return;
    const firstEnabled = firstEnabledIndex(visibleOptions);
    if (firstEnabled < 0) return;
    setActiveIndex((current) => {
      const startingIndex = current >= 0 && !visibleOptions[current]?.disabled ? current : firstEnabled;
      for (let attempt = 1; attempt <= visibleOptions.length; attempt += 1) {
        const candidate = (startingIndex + offset * attempt + visibleOptions.length) % visibleOptions.length;
        if (!visibleOptions[candidate]?.disabled) return candidate;
      }
      return firstEnabled;
    });
  }, [firstEnabledIndex, visibleOptions]);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
    if (returnFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const desiredHeight = Math.min(304, Math.max(ROW_HEIGHT, visibleOptions.length * ROW_HEIGHT + (usesSearch ? 56 : 0) + (allowClear ? ROW_HEIGHT : 0) + 8));
    const below = viewportHeight - rect.bottom - VIEWPORT_MARGIN;
    const above = rect.top - VIEWPORT_MARGIN;
    const placeBelow = below >= Math.min(desiredHeight, 132) || below >= above;
    const maxHeight = Math.max(ROW_HEIGHT, Math.min(desiredHeight, placeBelow ? below : above));
    const width = Math.min(Math.max(rect.width, 220), viewportWidth - VIEWPORT_MARGIN * 2);
    const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), viewportWidth - width - VIEWPORT_MARGIN);
    const top = placeBelow
      ? Math.min(rect.bottom + 4, viewportHeight - maxHeight - VIEWPORT_MARGIN)
      : Math.max(VIEWPORT_MARGIN, rect.top - maxHeight - 4);
    setPosition({ top, left, width, maxHeight });
  }, [allowClear, usesSearch, visibleOptions.length]);

  const schedulePosition = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      positionMenu();
    });
  }, [positionMenu]);

  const openMenu = useCallback((focusList = false) => {
    if (disabled) return;
    const selectedIndex = visibleOptions.findIndex((option) => option.value === value && !option.disabled);
    setActiveIndex(normalizeActiveIndex(selectedIndex));
    setOpen(true);
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: instanceId }));
    requestAnimationFrame(() => {
      positionMenu();
      if (usesSearch) searchRef.current?.focus();
      else if (focusList) menuRef.current?.focus();
    });
  }, [disabled, instanceId, normalizeActiveIndex, positionMenu, usesSearch, value, visibleOptions]);

  const choose = useCallback((next: T | null) => {
    onChange(next);
    close(true);
  }, [close, onChange]);

  /**
   * A Radix Dialog must prevent its outside-pointer dismissal for a menu
   * portalled to document.body. That prevention can suppress the synthetic
   * click following a touch/mouse interaction, so commit finite options on
   * pointerdown instead. Keep a zero-detail click fallback for assistive
   * technology activation.
   */
  const chooseFromPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>, next: T | null) => {
    if (event.button !== 0) return;
    event.preventDefault();
    choose(next);
  }, [choose]);

  const chooseFromClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, next: T | null) => {
    if (event.detail === 0) choose(next);
  }, [choose]);

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(true);
    }
  };

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") { event.preventDefault(); close(true); return; }
    if (event.key === "Tab") { close(false); onBlur?.(); return; }
    if (!visibleOptions.length) return;
    if (event.key === "Home") { event.preventDefault(); setActiveIndex(firstEnabledIndex(visibleOptions)); return; }
    if (event.key === "End") {
      event.preventDefault();
      const lastEnabled = [...visibleOptions].reverse().findIndex((option) => !option.disabled);
      setActiveIndex(lastEnabled < 0 ? -1 : visibleOptions.length - lastEnabled - 1);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveActiveIndex(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && activeIndex >= 0) {
      event.preventDefault();
      const option = visibleOptions[activeIndex];
      if (option && !option.disabled) choose(option.value);
    }
  };

  useEffect(() => {
    if (!open) return;
    setActiveIndex((current) => normalizeActiveIndex(current));
  }, [normalizeActiveIndex, open, visibleOptions]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${menuId}-${activeIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, menuId, open]);

  useEffect(() => {
    if (!open) return;
    const onOtherOpen = (event: Event) => { if ((event as CustomEvent<string>).detail !== instanceId) close(false); };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) close(false);
    };
    const onEscape = (event: KeyboardEvent) => { if (event.key === "Escape") close(true); };
    window.addEventListener(OPEN_EVENT, onOtherOpen);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onEscape);
    window.addEventListener("resize", schedulePosition);
    window.addEventListener("scroll", schedulePosition, true);
    window.visualViewport?.addEventListener("resize", schedulePosition);
    window.visualViewport?.addEventListener("scroll", schedulePosition);
    schedulePosition();
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      window.removeEventListener(OPEN_EVENT, onOtherOpen);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onEscape);
      window.removeEventListener("resize", schedulePosition);
      window.removeEventListener("scroll", schedulePosition, true);
      window.visualViewport?.removeEventListener("resize", schedulePosition);
      window.visualViewport?.removeEventListener("scroll", schedulePosition);
    };
  }, [close, instanceId, open, schedulePosition]);

  const localPortalRect = portalContainer?.getBoundingClientRect();
  const menuStyle: CSSProperties = portalContainer && localPortalRect
    ? {
      position: "absolute",
      top: position.top - localPortalRect.top,
      left: position.left - localPortalRect.left,
      width: position.width,
      maxHeight: position.maxHeight,
    }
    : { position: "fixed", top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight };
  const listHeight = Math.max(ROW_HEIGHT, position.maxHeight - (usesSearch ? 56 : 0) - (allowClear ? ROW_HEIGHT : 0));

  return (
    <div className={className}>
      {label && <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>}
      <button
        ref={triggerRef}
        id={triggerId ?? testId}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        onBlur={(event) => {
          const nextTarget = event.relatedTarget as Node | null;
          if (!nextTarget || !menuRef.current?.contains(nextTarget)) onBlur?.();
        }}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-required={required || undefined}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={`flex min-h-11 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-800 shadow-sm outline-none transition-colors focus:border-[#002975] focus:ring-2 focus:ring-[#002975]/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:shadow-none ${triggerClassName}`}
      >
        <span className="min-w-0 flex-1 truncate">{selected?.label ?? placeholder}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <div ref={menuRef} id={menuId} role="listbox" tabIndex={-1} aria-label={ariaLabel} aria-activedescendant={activeIndex >= 0 ? `${menuId}-${activeIndex}` : undefined} onKeyDown={onMenuKeyDown} style={menuStyle} data-anchored-select-menu="" data-testid={testId ? `${testId}-menu` : undefined} className={`z-[70] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ${menuClassName}`}>
          {usesSearch && <div className="border-b border-gray-100 p-2"><div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" /><input ref={searchRef} value={query} onChange={(event) => { setQuery(event.target.value); setActiveIndex(firstEnabledIndex(visibleOptions)); }} placeholder={searchPlaceholder ?? "ຄົ້ນຫາລາຍການ..."} className="min-h-10 w-full rounded-lg border border-gray-200 py-2 pl-9 pr-8 text-sm outline-none focus:border-[#002975] focus:ring-2 focus:ring-[#002975]/20" />{query && <button type="button" onClick={() => setQuery("")} aria-label="ລຶບຄຳຄົ້ນຫາ" className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center text-gray-500"><X className="h-4 w-4" /></button>}</div></div>}
          <div className="overflow-y-auto py-1" style={{ maxHeight: listHeight }}>
            {allowClear && value !== null && <button type="button" role="option" aria-selected={false} onPointerDown={(event) => chooseFromPointerDown(event, null)} onClick={(event) => chooseFromClick(event, null)} className="flex min-h-11 w-full items-center px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50">{clearLabel ?? placeholder}</button>}
            {visibleOptions.map((option, index) => <button key={`${typeof option.value}:${String(option.value)}`} id={`${menuId}-${index}`} type="button" role="option" aria-selected={option.value === value} disabled={option.disabled} onMouseEnter={() => setActiveIndex(index)} onPointerDown={(event) => chooseFromPointerDown(event, option.value)} onClick={(event) => chooseFromClick(event, option.value)} className={`flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${option.value === value ? "bg-blue-50 font-semibold text-[#002975]" : activeIndex === index ? "bg-gray-50 text-gray-900" : "text-gray-700 hover:bg-gray-50"}`}><span className="min-w-0 flex-1"><span className="block truncate">{option.label}</span>{option.description && <span className="mt-0.5 block line-clamp-2 text-xs font-normal leading-4 text-gray-500">{option.description}</span>}</span>{option.value === value && <Check className="h-4 w-4 shrink-0" />}</button>)}
            {!visibleOptions.length && <p className="px-3 py-4 text-center text-sm text-gray-500">{noResultsLabel ?? "ບໍ່ພົບລາຍການ"}</p>}
          </div>
        </div>,
        portalContainer ?? document.body,
      )}
    </div>
  );
}
